/**
 * VendorLinesSection 供應商 (S1.4, extended S2.1) — multi-vendor sourcing
 * lines with the exactly-one-primary invariant (主要供應商 US-266). The 80px
 * fixed action column hosts the set-primary + per-line 生效/停用 actions and
 * is itself dropped when neither is available; adding goes through
 * AddVendorLineModal from the header button, dropped on the same condition.
 *
 * US-148 (0708-J Must): every line carries a per-vendor 生效/停用 status —
 * inactive lines stay VISIBLE with a status chip (data status display;
 * hide-don't-disable does not apply to status display).
 *
 * S2.1 read-through columns: 聯絡人 / 電話 / Email joined from the vendor
 * master (primary contact per vendor, `renderDash` fallback — read-only, zero
 * write affordances) and 優先順序 rendered from the existing `isPrimary`
 * exactly-one-primary invariant (numeric priority = Q-M8).
 */

import { useMemo } from 'react';
import { Button, Dropdown, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { HistoryOutlined, MoreOutlined, PlusOutlined, StarOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { useTranslation } from 'react-i18next';
import TableContainer, { renderDash } from '@/components/common/TableContainer';
import PurpleTag from '@/components/common/PurpleTag';
import { apiClient, contactApi, purchaseOrderApi } from '@/services/api';
import { formatPhoneFromApiForDisplay } from '@/utils/phoneUtils';
import {
  selectLeadTimeObservations,
  type LeadTimeGoodsReceiptInput,
  type LeadTimePurchaseOrderInput,
  type LeadTimeSummary,
} from '@/lib/reorder/leadTime';
import type { ContactResponse } from '@/types/api/contact';
import type { GoodsReceipt } from '@/types/api/goodsReceipt';
import type { Product, ProductVendorLine } from '@/types/api/product';
import SectionHeader from './SectionHeader';

/**
 * Vendor-master contact pick for the read-through columns (S2.1): the active
 * primary contact, else the first active contact — never an inactive one.
 * Pure; exported for the model-level vitest.
 */
/* eslint-disable react-refresh/only-export-components -- pure join predicate
   exported alongside the section so the vitest exercises the EXACT pick the
   table renders (house CommercialSection precedent). */
export function pickVendorContact(
  contacts: ContactResponse[] | undefined
): ContactResponse | undefined {
  if (!contacts || contacts.length === 0) return undefined;
  return (
    contacts.find((contact) => contact.isPrimary && contact.isActive) ??
    contacts.find((contact) => contact.isActive)
  );
}
/* eslint-enable react-refresh/only-export-components */

// Read-only observed-lead-time feed (SR4.3): the last-3 PO→GR cycles per vendor.
// DERIVED-NEVER-STORED — recomputed per read from the PO + docket stores; no new
// storage. Worklist-scale mock fetch (no pagination pressure), /api/-prefixed
// static cacheKeys, shared across product-detail mounts.
const LEAD_TIME_PO_CACHE_KEY = '/api/purchase-orders?view=lead-time';
const LEAD_TIME_GR_CACHE_KEY = '/api/goods-receipts?view=lead-time';

/**
 * THE THREE WRITE HANDLERS ARE ALL OPTIONAL, and their absence is the hide
 * signal. No vendor-line write has a graph root while the line READ is
 * graph-served (`vendorLineWriteReachesTheRead`), so in that posture every one
 * of them would report a write nothing stored — and report it as a 200,
 * because the Postgres seed derives from the same fixture the mock gates on.
 * A control that succeeds over an unmoved row is worse than an absent one.
 *
 * They are optional TOGETHER because they share one cause. Round 4 found this
 * section gating one of the three on the capability and rendering the other
 * two unconditionally: the shape of that bug is exactly a per-control opt-in,
 * so the props are written so that adding a fourth vendor-line write without a
 * gate is the unusual thing to do rather than the default.
 */
interface VendorLinesSectionProps {
  product: Product;
  /** US-266 新增供應商 — absent ⇒ the header button is not rendered. */
  onAdd?: () => void;
  /** US-266 設為主要供應商 — absent ⇒ the row action is not rendered. */
  onSetPrimary?: (line: ProductVendorLine) => void;
  /** US-148 per-line 生效/停用 — absent ⇒ the row action is not rendered. */
  onToggleActive?: (line: ProductVendorLine) => void;
}

export const VendorLinesSection = ({
  product,
  onAdd,
  onSetPrimary,
  onToggleActive,
}: VendorLinesSectionProps) => {
  const { t } = useTranslation(['products', 'procurement', 'common']);
  const { token } = theme.useToken();

  // Observed lead time (實際前置期) — a read-only chip derived from real PO→GR
  // cycles (SR4.3). NEVER a v1 formula input; it is the v2-ladder capture point.
  const { data: purchaseOrders, loading: poLoading } = useRequest(
    async () => (await purchaseOrderApi.list({ page: 1, pageSize: 1000 })).data,
    { cacheKey: LEAD_TIME_PO_CACHE_KEY, staleTime: 60_000 }
  );
  const { data: goodsReceipts, loading: grLoading } = useRequest(
    async () => (await apiClient.get<{ data: GoodsReceipt[] }>('/goods-receipts')).data.data,
    { cacheKey: LEAD_TIME_GR_CACHE_KEY, staleTime: 60_000 }
  );
  const leadTimeReady = !poLoading && !grLoading && !!purchaseOrders && !!goodsReceipts;

  // S2.1 — vendor-master read-through join for 聯絡人/電話/Email. Per-vendor
  // failures degrade to renderDash (read-through fallback, never an error
  // surface). No cacheKey: the vendor set is per-product (dynamic-key leak
  // guard) and contacts must be fresh after vendor-master edits.
  const vendorIdsKey = useMemo(
    () =>
      [...new Set((product.vendorLines ?? []).map((line) => line.vendorId))]
        .sort((a, b) => a - b)
        .join(','),
    [product.vendorLines]
  );
  const { data: contactsByVendor } = useRequest(
    async () => {
      const vendorIds = vendorIdsKey ? vendorIdsKey.split(',').map(Number) : [];
      const entries = await Promise.all(
        vendorIds.map(async (vendorId) => {
          try {
            const response = await contactApi.list(vendorId);
            return [vendorId, response.data] as const;
          } catch {
            return [vendorId, [] as ContactResponse[]] as const;
          }
        })
      );
      return new Map<number, ContactResponse[]>(entries);
    },
    { refreshDeps: [vendorIdsKey], ready: vendorIdsKey.length > 0 }
  );

  /** vendorId → observed-lead-time summary over the last-3 completed cycles. */
  const leadTimeByVendor = useMemo(() => {
    const map = new Map<number, LeadTimeSummary>();
    if (!purchaseOrders || !goodsReceipts) return map;
    const poInputs: LeadTimePurchaseOrderInput[] = purchaseOrders.map((po) => ({
      id: po.id,
      vendorId: po.vendorId,
      // OQ-8: no `sentAt` on today's PO record → null falls back to createdAt.
      sentAt: null,
      createdAt: po.createdAt,
    }));
    const grInputs: LeadTimeGoodsReceiptInput[] = goodsReceipts.map((gr) => ({
      poId: gr.poId,
      status: gr.status,
      receiptDate: gr.receiptDate,
    }));
    for (const line of product.vendorLines ?? []) {
      if (!map.has(line.vendorId)) {
        map.set(line.vendorId, selectLeadTimeObservations(line.vendorId, poInputs, grInputs));
      }
    }
    return map;
  }, [purchaseOrders, goodsReceipts, product.vendorLines]);

  const lineActive = (line: ProductVendorLine): boolean => line.isActive !== false;

  /** Money cell — neutral numeric (no colour-by-value), '-' when blank. */
  const money = (value: number | null | undefined, currency: string) =>
    value == null ? renderDash(undefined) : `${currency} ${value.toFixed(2)}`;

  const columns: NonNullable<TableProps<ProductVendorLine>['columns']> = [
    {
      title: t('products:fields.vendor'),
      dataIndex: 'vendorName',
      key: 'vendorName',
      render: (name: string, line) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>{name}</span>
          {line.vendorSku && (
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {line.vendorSku}
            </Typography.Text>
          )}
        </div>
      ),
    },
    {
      // S2.1 read-through (vendor-master join) — primary contact name.
      title: t('products:vendorLine.contact'),
      key: 'vendorContact',
      width: 150,
      render: (_: unknown, line) =>
        renderDash(pickVendorContact(contactsByVendor?.get(line.vendorId))?.name),
    },
    {
      // S2.1 read-through — primary contact phone.
      title: t('products:vendorLine.contactPhone'),
      key: 'vendorContactPhone',
      width: 160,
      render: (_: unknown, line) => {
        const contact = pickVendorContact(contactsByVendor?.get(line.vendorId));
        return renderDash(
          contact
            ? formatPhoneFromApiForDisplay(contact, t('products:vendorLine.ext')) || undefined
            : undefined
        );
      },
    },
    {
      // S2.1 read-through — primary contact email.
      title: t('products:vendorLine.contactEmail'),
      key: 'vendorContactEmail',
      width: 200,
      render: (_: unknown, line) =>
        renderDash(pickVendorContact(contactsByVendor?.get(line.vendorId))?.email),
    },
    {
      title: t('products:fields.unitCost'),
      dataIndex: 'unitCost',
      key: 'unitCost',
      width: 160,
      render: (value: number, line) => `${line.currency} ${value.toFixed(2)}`,
    },
    {
      // W7-B3 訂購單位 (2026-08-27 session §2) — the vendor's selling unit,
      // CAPTURE/DISPLAY ONLY: no qty conversion against stockUnit anywhere
      // (decision-gated — Q5). Dash when the vendor never named one.
      title: t('products:fields.orderUnit'),
      dataIndex: 'orderUnit',
      key: 'orderUnit',
      width: 110,
      render: (value: string | undefined) => renderDash(value),
    },
    {
      // H51 上次採購價 (US-285) — the last transacted price (rolls on GR post).
      title: t('procurement:vendorLine.lastPurchasePrice'),
      dataIndex: 'lastPurchasePrice',
      key: 'lastPurchasePrice',
      width: 150,
      render: (value: number | null | undefined, line) => money(value, line.currency),
    },
    {
      // H51 下次採購價 (US-285) — the price the next PO prefills with. When it
      // diverges from 上次採購價 a neutral 已調整 chip surfaces the change (no
      // colour-by-value — the numeric stays neutral per the house rule).
      title: t('procurement:vendorLine.nextPurchasePrice'),
      dataIndex: 'nextPurchasePrice',
      key: 'nextPurchasePrice',
      width: 170,
      render: (value: number | null | undefined, line) => {
        const changed =
          value != null && line.lastPurchasePrice != null && value !== line.lastPurchasePrice;
        return (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <span>{money(value, line.currency)}</span>
            {changed && (
              <Tag style={{ margin: 0, width: 'fit-content' }}>
                {t('procurement:vendorLine.priceChanged')}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      // 最低訂購量 MOQ (US-285) — neutral numeric, advisory data only.
      title: t('procurement:vendorLine.moq'),
      dataIndex: 'moq',
      key: 'moq',
      width: 110,
      render: (value: number | null | undefined) =>
        value == null ? renderDash(undefined) : value,
    },
    {
      // Configured 前置期 (top) + observed 實際前置期 chip (SR4.3): the last-3
      // PO→GR mean, or a 資料不足 tone when fewer than 3 cycles exist. The chip is
      // read-only — the observed number never feeds the v1 suggestion formula.
      title: t('products:fields.leadTime'),
      dataIndex: 'leadTimeDays',
      key: 'leadTimeDays',
      width: 150,
      render: (value: number | undefined, line) => {
        const summary = leadTimeByVendor.get(line.vendorId);
        return (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <span>{value == null ? renderDash(undefined) : t('products:days', { count: value })}</span>
            {leadTimeReady && summary && (
              <Tag
                icon={<HistoryOutlined />}
                bordered={summary.sufficient}
                style={{
                  margin: 0,
                  width: 'fit-content',
                  color: summary.sufficient ? undefined : token.colorTextTertiary,
                }}
              >
                {summary.sufficient && summary.meanDays != null
                  ? t('products:days', { count: Math.round(summary.meanDays) })
                  : t('common:messages.noData')}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      // 優先順序 (S2.1) — rendered from the existing `isPrimary`
      // exactly-one-primary invariant (numeric priority = Q-M8).
      title: t('products:fields.priority'),
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      width: 160,
      render: (isPrimary: boolean) =>
        isPrimary ? <PurpleTag>{t('products:preferred')}</PurpleTag> : renderDash(undefined),
    },
    {
      // US-148 — per-line 生效/停用 chip (inactive stays visible)
      title: t('products:vendorLine.status'),
      key: 'lineStatus',
      width: 110,
      render: (_: unknown, line) =>
        lineActive(line) ? (
          <Tag color="success">{t('common:status.active')}</Tag>
        ) : (
          <Tag>{t('common:status.inactive')}</Tag>
        ),
    },
  ];

  // HIDE, don't disable, and hide the CONTAINER too. When neither row write is
  // available the capability does not exist yet, so the row offers nothing
  // rather than a greyed promise — and an action column whose dropdown opens
  // empty is the same broken promise one click further in. The hint under the
  // header carries the explanation once, instead of every row a tooltip.
  const rowWriteAvailable = !!onSetPrimary || !!onToggleActive;
  if (rowWriteAvailable) {
    columns.push({
      title: t('products:fields.action'),
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, line) => (
        <Dropdown
          menu={{
            items: [
              ...(onSetPrimary
                ? [
                    {
                      key: 'set-primary',
                      label: t('products:setPreferred'),
                      icon: <StarOutlined />,
                      disabled: line.isPrimary || !lineActive(line) || !product.isActive,
                      onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                        e.domEvent.stopPropagation();
                        onSetPrimary(line);
                      },
                    },
                  ]
                : []),
              ...(onToggleActive
                ? [
                    {
                      key: 'toggle-active',
                      label: lineActive(line)
                        ? t('products:vendorLine.deactivate')
                        : t('products:vendorLine.activate'),
                      danger: lineActive(line),
                      style: !lineActive(line) ? { color: token.colorSuccess } : undefined,
                      disabled: !product.isActive || (lineActive(line) && line.isPrimary),
                      onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                        e.domEvent.stopPropagation();
                        onToggleActive(line);
                      },
                    },
                  ]
                : []),
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('products:actionsFor', { name: line.vendorName })}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    });
  }

  // One sentence for the whole group, said once under the header — not three
  // tooltips saying the same thing about the same missing root.
  const writesAvailable = !!onAdd && rowWriteAvailable;

  return (
    <div>
      <SectionHeader
        title={t('products:detail.sections.vendors')}
        subtitle={
          writesAvailable
            ? t('products:vendorsHint')
            : `${t('products:vendorsHint')} ${t('products:vendorLine.editPending')}`
        }
        extra={
          onAdd ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!product.isActive}
              onClick={onAdd}
            >
              {t('products:addVendor')}
            </Button>
          ) : undefined
        }
      />
      <TableContainer>
        <Table<ProductVendorLine>
          rowKey="id"
          dataSource={product.vendorLines ?? []}
          columns={columns}
          pagination={false}
          rowClassName={(line) => (lineActive(line) ? '' : 'inactive-row')}
          locale={{ emptyText: t('products:noVendors') }}
          scroll={{ x: rowWriteAvailable ? 1870 : 1790 }}
        />
      </TableContainer>
    </div>
  );
};

export default VendorLinesSection;
